import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, BookOpen, ArrowUpRight, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { fetchGuides, fetchGuideCategories } from '../lib/api';

const LegalGuides = () => {
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState('All');
  const [guides, setGuides] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGuideCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (cat && cat !== 'All') params.category = cat;
    if (query.trim()) params.q = query.trim();
    const t = setTimeout(() => {
      fetchGuides(params)
        .then(setGuides)
        .catch(() => toast.error('Could not load guides. Please retry.'))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query, cat]);

  return (
    <div>
      <section className="bg-[#0F1B2D] text-[#F8F2E4] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.3em] text-[#E89530] mb-5">LIBRARY</p>
          <h1 className="font-serif-besley text-5xl md:text-6xl mb-5">Legal guides in plain words</h1>
          <p className="text-[#C9BFA8] text-lg max-w-2xl">
            Read jargon-free explainers on the laws that affect everyday Indians — from rent agreements to cyber fraud.
          </p>
        </div>
      </section>

      <section className="bg-[#F8F2E4] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="relative max-w-xl mb-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guides…"
              className="pl-11 h-12 bg-white border-[#E8DCC0] focus-visible:ring-[#2D4373]"
            />
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`px-4 py-2 rounded-full text-sm border transition-all ${
                  cat === c
                    ? 'bg-[#0F1B2D] text-[#F8F2E4] border-[#0F1B2D]'
                    : 'bg-white text-[#0F1B2D] border-[#E8DCC0] hover:border-[#0F1B2D]'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24 text-[#5a5a5a]">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {guides.map((g) => (
                <Link
                  key={g.id}
                  to={`/legal-guides/${g.id}`}
                  className="pillar-card bg-[#EDE3CC] rounded-2xl p-7 flex flex-col group"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#E8DCC0] flex items-center justify-center mb-5">
                    <BookOpen className="w-5 h-5 text-[#0F1B2D]" strokeWidth={1.6} />
                  </div>
                  <Badge variant="secondary" className="self-start bg-white text-[#0F1B2D] border border-[#D6CAB0] mb-4">
                    {g.category}
                  </Badge>
                  <h3 className="font-serif-besley text-[26px] leading-[1.2] text-[#0F1B2D] mb-4">
                    {g.title}
                  </h3>
                  <p className="text-[15px] text-[#3a3a3a] leading-relaxed mb-6">{g.excerpt}</p>
                  <div className="mt-auto flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5 text-[#5a5a5a]">
                      <Clock className="w-4 h-4" /> {g.readTime}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[#A36B1F] group-hover:text-[#E89530] transition-colors font-medium">
                      Read <ArrowUpRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {!loading && guides.length === 0 && (
            <div className="text-center py-20 text-[#5a5a5a]">No guides match your search.</div>
          )}
        </div>
      </section>
    </div>
  );
};

export default LegalGuides;
